import { Box, IconButton, Link as MuiLink, Tooltip, Typography, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { startCase } from 'lodash';
import { SectionCard } from '@modules/talents/components/SectionCard';
import { SectionTitle } from '@modules/talents/components/SectionTitle';
import { AvatarStatusFavorite } from '@modules/talents/components/AvatarStatusFavorite';
import { useState } from 'react';

interface TalentCardProps {
    talent: {
        _id: string;
        firstName?: string;
        lastName?: string;
        photo?: string;
        logo?: string;
        city?: string;
        yearsOfExperience?: number;
        currentStatusUser?: string;
        email?: string;
        phoneNumber?: string;
        principalProfession?: { professionName?: string } | null;
        upercutFavorites?: any;
    };
}


const CopyField = ({ label, value, href }: { label: string; value: string; href?: string }) => {
    const theme = useTheme();
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // clipboard not available
        }
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
            {href ? (
                <MuiLink component={Link} to={href}  variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', wordBreak: 'break-all' }}>
                    {value}
                </MuiLink>
            ) : (
                <Typography  variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', wordBreak: 'break-all' }}>{value}</Typography>
            )}
            <Tooltip title={copied ? 'Copié !' : 'Copier'} placement="top">
                <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
                    {copied ? (
                        <i className="fi fi-br-check" style={{ fontSize: 14, color: theme.palette.successGreen.main, display: 'flex' }} />
                    ) : (
                        <i className="fi fi-br-copy" style={{ fontSize: 14, color: theme.palette.text.secondary, display: 'flex' }} />
                    )}
                </IconButton>
            </Tooltip>
        </Box>
    );
};


const TalentCard = ({ talent }: TalentCardProps) => {
    const theme = useTheme();
    const fullName = [talent.firstName, talent.lastName].filter(Boolean).join(' ') || '-';
    const avatar = talent.photo || talent.logo;

    return (
        <SectionCard>
            <SectionTitle title="Candidat" />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                    <AvatarStatusFavorite
                        talentIds={talent._id}
                        currentStatus={(talent.currentStatusUser as any) ?? 'nothing'}
                        image={avatar ?? undefined}
                        upercutFavorites={talent?.upercutFavorites}
                        alt={fullName}
                        size="sm"
                        disabled
                        avatarSx={{ borderRadius: '50%' }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography
                                variant="subtitle2"
                                fontWeight={700}
                                component={Link}
                                to={`/talents/${talent._id}/detail`}
                                sx={{ color: theme.palette.neutralWhite.main, textDecoration: 'underline', textDecorationColor: theme.palette.neutralWhite.main }}
                            >
                                {fullName}
                            </Typography>
                        </Box>
                        {talent.principalProfession?.professionName && (
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }} noWrap>
                                {talent.principalProfession.professionName}
                            </Typography>
                        )}
                        {(talent.city || talent.yearsOfExperience != null) && (
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                                {[
                                    talent.city ? startCase(talent.city.toLowerCase()) : null,
                                    talent.yearsOfExperience != null ? `${talent.yearsOfExperience} an${talent.yearsOfExperience > 1 ? 's' : ''} exp.` : null,
                                ].filter(Boolean).join(' · ')}
                            </Typography>
                        )}
                            {talent.email && (
                                <CopyField
                                    label="Email"
                                    value={talent.email}
                                    href={`mailto:${talent.email}`}
                                />
                            )}
                            {talent.phoneNumber && (
                                <CopyField
                                    label="Téléphone"
                                    value={talent.phoneNumber}
                                    href={`tel:${talent.phoneNumber}`}
                                />
                            )}
                    </Box>
                </Box>
        </SectionCard>
    );
};

export default TalentCard;
